import { IEncodedPersistedAction, StreamPosition, makeStreamPosition } from '@engoo/realtime-protocol/persisted';
import { SyncState } from 'app/models/rtc/sync/constants';
import { ActionStreamEvents, IActionStream, IMetadata } from 'app/services/rtc/sync/ActionStream';
import {
  Events,
  IDocumentManager,
  IEventTypeTable,
  EventHandler,
} from 'app/services/rtc/sync/documentmanagers';
import { EventEmitter } from 'events';

export const RETRY_INTERVAL_MS = 1000;

// Shared functionality for DocumentManagers, not an interface
export abstract class AbstractDocumentManager<DocumentT, ActionT>
  implements IDocumentManager<DocumentT, ActionT> {
  // Committed snapshot
  protected snapshot: DocumentT;
  protected snapshotVersion: number;

  // snapshot + localActions
  protected latestSnapshot: DocumentT;

  // Action transmit/receiver
  protected readonly actionStream: IActionStream<ActionT>;

  // Local event interface
  private readonly eventEmitter: EventEmitter = new EventEmitter();

  private metadataInternal: IMetadata;
  private statusInternal: SyncState = SyncState.closed;

  protected constructor(
    actionStream: IActionStream<ActionT>,
    snapshot: DocumentT,
    metadata: IMetadata | undefined,
    snapshotVersion: number,
  ) {
    this.actionStream    = actionStream;
    this.snapshot        = snapshot;
    this.snapshotVersion = snapshotVersion;

    this.latestSnapshot = snapshot;

    // ActionStreams are lazily realised. It is likely that there is no
    // existing metadata for this stream. Assign a sensible default.
    if (metadata) {
      this.metadataInternal = metadata;
    } else {
      this.metadataInternal = {
        blockedReasons: [],
      };
    }

    this.onMetadataChanged = this.onMetadataChanged.bind(this);
    this.actionStream.on(ActionStreamEvents.METADATA_CHANGED, this.onMetadataChanged);
  }

  public start() {
    this.setStatus(SyncState.open);
    this.emit(Events.METADATA_CHANGED, this.metadata);

    const snapshotVersion = this.snapshotVersion >= 0 ?
      makeStreamPosition(this.snapshotVersion) :
      undefined;

    void this.subscribeToStream(snapshotVersion);
  }

  public getLatestSnapshot() {
    return this.latestSnapshot;
  }

  public addListener<E extends Events>(
    eventName: E,
    handler: EventHandler<IEventTypeTable<DocumentT, ActionT>[E]>,
  ) {
    this.eventEmitter.addListener(eventName, handler);
  }

  public removeListener<E extends Events>(
    eventName: E,
    handler: EventHandler<IEventTypeTable<DocumentT, ActionT>[E]>,
  ) {
    this.eventEmitter.removeListener(eventName, handler);
  }

  public prependListener<E extends Events>(
    eventName: E,
    handler: EventHandler<IEventTypeTable<DocumentT, ActionT>[E]>,
  ) {
    this.eventEmitter.prependListener(eventName, handler);
  }

  public shutdown(): void {
    this.actionStream.removeListener(ActionStreamEvents.METADATA_CHANGED, this.onMetadataChanged);
    this.setStatus(SyncState.closed);
  }

  public get metadata(): Readonly<IMetadata> {
    return this.metadataInternal;
  }

  public get status(): SyncState {
    return this.statusInternal;
  }

  // Add one action, typically on reception
  protected abstract onAction(x: IEncodedPersistedAction & ActionT): void;

  // Add many actions, typically for a catchup
  protected abstract onActions(actions: Array<IEncodedPersistedAction & ActionT>): void;

  protected emit<E extends Events>(
    eventName: E,
    data?: IEventTypeTable<DocumentT, ActionT>[E],
  ): void {
    this.eventEmitter.emit(eventName, data);
  }

  protected async subscribeToStream(lastPosition?: StreamPosition): Promise<void> {
    const { metadata, deltaActions } = await this.actionStream.subscribe(lastPosition);
    this.onActions(deltaActions);
    this.onMetadataChanged(metadata);
    this.setStatus(SyncState.synced);
  }

  private onMetadataChanged(metadata: IMetadata): void {
    this.metadataInternal = metadata;
    this.emit(Events.METADATA_CHANGED, metadata);
  }

  private setStatus(status: SyncState) {
    this.statusInternal = status;

    switch (status) {
      case SyncState.open: this.emit(Events.OPENED, {}); break;
      case SyncState.synced: this.emit(Events.SYNCED, {}); break;
      case SyncState.closed: this.emit(Events.CLOSED, {}); break;
    }
  }
}
